import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Camera, Lock, Unlock, MessageSquare, Pause, Play, Radio,
} from 'lucide-react';
import { API_URL } from '@/services/apiClient';
import { io, type Socket } from 'socket.io-client';
import { StudentLiveView } from './StudentLiveView';
import {
  LiveScreensLayout,
  ScreenTile,
  StudentScreenModal,
  type ScreenTileData,
  type FilterMode,
  type GridSize,
  formatLastSeen,
} from '@/components/shared';

/* ── Types ────────────────────────────────────────────────────────── */

interface AdminTile extends ScreenTileData {
  userId: string;
  studentId: string | null;
  lastSeen: string | null;
  screenshotCapturedAt: string | null;
  screenshotIsHarmful: boolean;
}

interface LiveScreensData {
  tiles: AdminTileRaw[];
  summary: { total: number; online: number; active: number; flagged: number };
}

/** Raw shape from the REST endpoint. */
interface AdminTileRaw {
  userId: string;
  studentId: string | null;
  name: string;
  isOnline: boolean;
  isActive: boolean;
  isFlagged: boolean;
  currentDomain: string | null;
  currentUrl: string | null;
  currentPageTitle: string | null;
  currentEventType: string | null;
  lastSeen: string | null;
  screenshotUrl: string | null;
  screenshotCapturedAt: string | null;
  screenshotIsHarmful: boolean;
  openAlertCount: number;
}

/** Map a raw API tile to the shared ScreenTileData + admin extras. */
function toAdminTile(raw: AdminTileRaw): AdminTile {
  return {
    id: raw.userId,
    userId: raw.userId,
    studentId: raw.studentId,
    name: raw.name,
    isOnline: raw.isOnline || raw.isActive,
    isFlagged: raw.isFlagged,
    currentDomain: raw.currentDomain,
    currentPageTitle: raw.currentPageTitle,
    currentUrl: raw.currentUrl,
    currentEventType: raw.currentEventType,
    alertCount: raw.openAlertCount,
    timestampLabel: raw.screenshotCapturedAt ?? raw.lastSeen,
    screenshotUrl: raw.screenshotUrl,
    screenshotIsHarmful: raw.screenshotIsHarmful,
    screenshotCapturedAt: raw.screenshotCapturedAt,
    lastSeen: raw.lastSeen,
  };
}

/* ── Admin actions ────────────────────────────────────────────────── */

async function sendStudentAction(userId: string, type: string, payload?: Record<string, string>) {
  const token = sessionStorage.getItem('authToken');
  try {
    const res = await fetch(`${API_URL}/dashboard/school-admin/students/${userId}/action`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ type, payload }),
    });
    return res.ok;
  } catch { return false; }
}

async function requestCapture(userId: string) {
  const token = sessionStorage.getItem('authToken');
  try {
    const res = await fetch(`${API_URL}/dashboard/school-admin/students/${userId}/request-capture`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.ok;
  } catch { return false; }
}

/* ── Admin action toolbar (rendered inside shared modal) ──────────── */

function AdminActions({ tile, dm }: { tile: AdminTile; dm: boolean }) {
  const [actionBusy, setActionBusy] = useState<string | null>(null);
  const [messageText, setMessageText] = useState('');
  const [showMessageInput, setShowMessageInput] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [showLiveView, setShowLiveView] = useState(false);

  const doAction = async (type: string, payload?: Record<string, string>) => {
    setActionBusy(type);
    const ok = await sendStudentAction(tile.userId, type, payload);
    if (ok) {
      if (type === 'lock_screen') setIsLocked(true);
      if (type === 'unlock_screen') setIsLocked(false);
      if (type === 'pause_capture') setIsPaused(true);
      if (type === 'resume_capture') setIsPaused(false);
    }
    setActionBusy(null);
  };

  const btnCls = (active?: boolean) =>
    `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
      active
        ? 'bg-amber-100 text-amber-800 hover:bg-amber-200'
        : (dm ? 'bg-slate-700 text-slate-200 hover:bg-slate-600' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-100')
    }`;

  return (
    <>
      {/* Action toolbar */}
      <div className={`flex items-center gap-2 px-5 py-3 border-t flex-wrap ${dm ? 'border-slate-700 bg-slate-800/50' : 'border-slate-100 bg-slate-50'}`}>
        <button
          disabled={actionBusy !== null}
          onClick={async () => { setActionBusy('capture'); await requestCapture(tile.userId); setActionBusy(null); }}
          className={`${btnCls()} ${actionBusy === 'capture' ? 'opacity-50' : ''}`}
        >
          <Camera className="h-3.5 w-3.5" />
          {actionBusy === 'capture' ? 'Requesting…' : 'Capture Now'}
        </button>

        <button
          disabled={actionBusy !== null}
          onClick={() => doAction(isLocked ? 'unlock_screen' : 'lock_screen')}
          className={`${btnCls(isLocked)} ${actionBusy === 'lock_screen' || actionBusy === 'unlock_screen' ? 'opacity-50' : ''}`}
        >
          {isLocked ? <Unlock className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
          {isLocked ? 'Unlock Screen' : 'Lock Screen'}
        </button>

        <button
          disabled={actionBusy !== null}
          onClick={() => doAction(isPaused ? 'resume_capture' : 'pause_capture')}
          className={`${btnCls(isPaused ? false : undefined)} ${
            isPaused ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200' : ''
          } ${actionBusy === 'pause_capture' || actionBusy === 'resume_capture' ? 'opacity-50' : ''}`}
        >
          {isPaused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
          {isPaused ? 'Resume Monitoring' : 'Pause Monitoring'}
        </button>

        <button
          disabled={actionBusy !== null}
          onClick={() => setShowMessageInput(!showMessageInput)}
          className={btnCls()}
        >
          <MessageSquare className="h-3.5 w-3.5" />
          Send Message
        </button>

        {tile.isOnline && (
          <button
            onClick={() => setShowLiveView(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors bg-red-600 text-white hover:bg-red-700"
          >
            <Radio className="h-3.5 w-3.5" />
            Live View
          </button>
        )}
      </div>

      {/* Message input */}
      {showMessageInput && (
        <div className={`flex items-center gap-2 px-5 py-2.5 border-t ${dm ? 'border-slate-700 bg-slate-800/30' : 'border-slate-100 bg-slate-50/50'}`}>
          <input
            type="text"
            value={messageText}
            onChange={e => setMessageText(e.target.value)}
            placeholder="Type a message to send to the student…"
            className={`flex-1 px-3 py-1.5 rounded-lg text-sm outline-none border ${dm ? 'bg-slate-800 border-slate-600 text-slate-200 placeholder:text-slate-500' : 'bg-white border-slate-200 text-slate-800 placeholder:text-slate-400'}`}
            onKeyDown={e => {
              if (e.key === 'Enter' && messageText.trim()) {
                doAction('send_message', { text: messageText.trim() });
                setMessageText('');
                setShowMessageInput(false);
              }
            }}
          />
          <button
            disabled={!messageText.trim() || actionBusy === 'send_message'}
            onClick={() => {
              if (messageText.trim()) {
                doAction('send_message', { text: messageText.trim() });
                setMessageText('');
                setShowMessageInput(false);
              }
            }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
              messageText.trim()
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : (dm ? 'bg-slate-700 text-slate-500' : 'bg-slate-100 text-slate-400')
            } transition-colors`}
          >
            Send
          </button>
        </div>
      )}

      {/* Live view overlay */}
      {showLiveView && (
        <StudentLiveView
          studentId={tile.userId}
          studentName={tile.name}
          dm={dm}
          onClose={() => setShowLiveView(false)}
        />
      )}
    </>
  );
}

/* ── Main component ───────────────────────────────────────────────── */

export function LiveScreensView({ dm, onViewAlerts }: { dm: boolean; onViewAlerts?: (name: string) => void }) {
  const [tiles, setTiles] = useState<AdminTile[]>([]);
  const [rawSummary, setRawSummary] = useState({ total: 0, online: 0, active: 0, flagged: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState<FilterMode>('all');
  const [search, setSearch] = useState('');
  const [gridSize, setGridSize] = useState<GridSize>('medium');
  const [focused, setFocused] = useState<AdminTile | null>(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const REFRESH_INTERVAL = 15_000;

  const fetchData = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    const token = sessionStorage.getItem('authToken');
    try {
      const res = await fetch(`${API_URL}/dashboard/school-admin/live-screens`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const body: LiveScreensData = await res.json();
        setTiles(body.tiles.map(toAdminTile));
        setRawSummary(body.summary);
        setLastRefresh(new Date());
      }
    } catch {/* */} finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    intervalRef.current = setInterval(() => fetchData(), REFRESH_INTERVAL);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [fetchData]);

  // Socket.IO: real-time screenshot pushes
  const socketRef = useRef<Socket | null>(null);
  useEffect(() => {
    const token = sessionStorage.getItem('authToken');
    if (!token) return;

    const schoolId = localStorage.getItem('schoolId');
    const socket = io(API_URL.replace('/api', ''), { auth: { token } });
    socketRef.current = socket;

    socket.on('connect', () => {
      if (schoolId) socket.emit('join_room', `school_${schoolId}`);
    });

    socket.on('screen_update', (update: Partial<AdminTileRaw> & { userId?: string; studentId?: string }) => {
      setTiles(prev =>
        prev.map(t =>
          (t.userId === update.userId || t.studentId === update.studentId)
            ? { ...t, ...update, isOnline: true }
            : t
        )
      );
      setLastRefresh(new Date());
    });

    return () => { socket.disconnect(); socketRef.current = null; };
  }, []);

  const filtered = tiles.filter(tile => {
    if (filter === 'online' && !tile.isOnline) return false;
    if (filter === 'flagged' && !tile.isFlagged && !tile.screenshotIsHarmful) return false;
    if (search && !tile.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const summary = {
    total: rawSummary.total,
    online: rawSummary.online,
    flagged: rawSummary.flagged,
  };

  return (
    <>
      <LiveScreensLayout
        dm={dm}
        summary={summary}
        filter={filter}
        setFilter={setFilter}
        search={search}
        setSearch={setSearch}
        gridSize={gridSize}
        setGridSize={setGridSize}
        lastRefresh={lastRefresh}
        refreshing={refreshing}
        onRefresh={() => fetchData(true)}
        loading={loading}
        isEmpty={filtered.length === 0}
        filteredCount={filtered.length}
      >
        {filtered.map(tile => (
          <ScreenTile
            key={tile.userId}
            tile={tile}
            dm={dm}
            gridSize={gridSize}
            onClick={() => setFocused(tile)}
          />
        ))}
      </LiveScreensLayout>

      {focused && (
        <StudentScreenModal
          name={focused.name}
          isOnline={focused.isOnline}
          isFlagged={focused.isFlagged}
          currentDomain={focused.currentDomain}
          currentPageTitle={focused.currentPageTitle}
          currentUrl={focused.currentUrl}
          currentEventType={focused.currentEventType}
          screenshotUrl={focused.screenshotUrl}
          screenshotIsHarmful={focused.screenshotIsHarmful}
          statusText={
            focused.isOnline
              ? 'Online now'
              : focused.lastSeen
              ? `Last seen ${formatLastSeen(focused.lastSeen)}`
              : 'Offline'
          }
          statusDotClass={focused.isOnline ? 'bg-emerald-500' : focused.lastSeen ? 'bg-amber-400' : 'bg-slate-400'}
          dm={dm}
          onClose={() => setFocused(null)}
          headerExtra={
            focused.alertCount > 0 ? (
              <button
                onClick={() => { setFocused(null); onViewAlerts?.(focused.name); }}
                style={{
                  appearance: 'none', WebkitAppearance: 'none', border: 'none',
                  cursor: 'pointer', padding: '2px 8px', borderRadius: '9999px',
                  fontSize: '0.75rem', fontWeight: 500,
                  background: '#fee2e2', color: '#b91c1c',
                  transition: 'background 0.15s ease',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#fecaca'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#fee2e2'; }}
              >
                {focused.alertCount} open alert{focused.alertCount !== 1 ? 's' : ''} →
              </button>
            ) : undefined
          }
          actions={<AdminActions tile={focused} dm={dm} />}
          infoCells={[
            { label: 'Current Site', value: focused.currentDomain || '—' },
            { label: 'Activity', value: focused.currentEventType?.replace(/_/g, ' ') || '—' },
            { label: 'Last Seen', value: focused.lastSeen ? formatLastSeen(focused.lastSeen) : '—' },
            { label: 'Open Alerts', value: String(focused.alertCount) },
          ]}
        />
      )}
    </>
  );
}
